import { AxiosErrorResponse } from "@/@types/Errors";
import { Input } from "@/components/shared/Input/Input";
import { Button } from "@/components/ui/button";
import { SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "@/hooks/use-toast";
import useModalStore from "@/store/useModalStore";
import { normalizeUrlWhenPaste } from "@/utils/normalize";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Loader2 } from "lucide-react";
import React from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { z } from "zod";
import { CreateLinkRequest, Link } from "@/@types/Links";
import { LinksService } from "@/services/links.service";

export function CreateLink() {
  const { t } = useTranslation(["links", "common"]);
  const { closeModal } = useModalStore();
  const { id } = useParams();
  const queryClient = useQueryClient();

  const CreateLinkSchema = z.object({
    name: z
      .string()
      .min(1, t("common:required_field"))
      .max(
        120,
        t("common:validation.acceptButtonText.tooLong", { count: 120 })
      ),
    redirect_url: z
      .string()
      .min(1, t("common:required_field"))
      .url(t("common:invalid_url")),
    available_affiliates: z.boolean(),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<CreateLinkRequest>({
    shouldUnregister: false,
    resolver: zodResolver(CreateLinkSchema),
    defaultValues: {
      name: "",
      redirect_url: "",
      available_affiliates: false,
    },
  });

  const defaultValues = watch();

  const { mutate, isPending: isCreatingLink } = useMutation({
    mutationFn: (data: CreateLinkRequest) =>
      LinksService.createLink({ data, productId: id }),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: [`links-${id}`] });

      const previousQueries = queryClient.getQueriesData({
        queryKey: [`links-${id}`],
      });

      return { previousQueries };
    },
    onSuccess: (serverLink, variables, context) => {
      if (serverLink && context?.previousQueries) {
        queryClient.setQueriesData(
          { queryKey: [`links-${id}`], exact: false },
          (oldData: any) => {
            if (!oldData) return oldData;

            return {
              ...oldData,
              data: [serverLink.data, ...oldData.data],
            };
          }
        );
      }

      toast({
        variant: "success",
        description: t("links:saved_link"),
      });

      reset();
      closeModal("create-link");
    },
    onError: (error: AxiosError<AxiosErrorResponse>, variables, context) => {
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }

      toast({
        variant: "error",
        description:
          error?.response?.data?.message || t("common:data_listing_error"),
      });
    },
  });

  const onSubmit = async (data: CreateLinkRequest) => {
    mutate(data);
  };

  return (
    <section className="sm:overflow-y-hidden flex flex-col justify-between h-full">
      <SheetHeader className="p-6" data-cy="modal-create-link">
        <SheetTitle className="flex gap-2">{t("create_link")} </SheetTitle>
      </SheetHeader>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col justify-between h-full"
      >
        <section className="flex flex-col gap-4 px-6">
          <Input
            {...register("name")}
            label={t("common:name_single")}
            placeholder={t("links:enter_link_name")}
            data-cy="input-link-name"
            helperText={errors.name?.message}
          />
          <Input
            {...register("redirect_url")}
            label={t("redirect_url")}
            placeholder={t("links:enter_redirect_url")}
            data-cy="input-link-redirect-url"
            helperText={errors.redirect_url?.message}
            onFocus={(e) => {
              if (!e.target.value.startsWith("https://")) {
                e.target.value = "https://";
                setValue("redirect_url", "https://");
              }
            }}
            onPaste={(e) => {
              const finalValue = normalizeUrlWhenPaste(e);
              setValue("redirect_url", finalValue);
            }}
          />
          <div className="flex gap-2 items-center">
            <Switch
              checked={defaultValues?.available_affiliates}
              onCheckedChange={(value) =>
                setValue("available_affiliates", value)
              }
              data-cy="switch-link-available-affiliates"
            />
            <Label>{t("available_for_affiliates")}</Label>
          </div>
        </section>
        <div className="flex border-t items-center justify-end gap-4 px-6 py-4 w-full max-sm:pb-12">
          <div className="flex gap-2 justify-end items-end">
            <Button
              variant="ghost"
              onClick={() => closeModal("create-link")}
              type="button"
            >
              {t("common:cancel")}
            </Button>
            <Button
              data-cy="btn-save-link"
              type="submit"
              disabled={isCreatingLink}
            >
              {isCreatingLink ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="animate-spin" />
                  {t("common:saving")}
                </span>
              ) : (
                t("links:save_link")
              )}
            </Button>
          </div>
        </div>
      </form>
    </section>
  );
}
